import { redirect } from "react-router"
import type { Session } from "@supabase/supabase-js"
import { supabase } from "../../lib/supabase/client"
import { buildSignInRedirect } from "@/lib/auth/redirect"
import { isPendingReportShareAccountSetup } from "@/components/auth/auth-service"
import {
  fetchActingOrgSession,
  type ActingOrgSession,
} from "@/lib/auth/acting-org-service"

export interface AuthSession {
  session: Session
  userId: string
  email: string
  organizationId?: string | null
  hasOrganization: boolean
  /** Set only when an internal_user is acting as an organization. */
  actingOrg: ActingOrgSession | null
}

// Simple in-memory cache for current navigation
let sessionCache: {
  auth: AuthSession | null
  timestamp: number
  error: boolean
} | null = null

const CACHE_DURATION_MS = 300000 // 5 minutes

// Unified org resolution (used by requireAuth here and requireSessionAndOrg in
// useCurrentOrganization): public.users.organization_id is authoritative;
// auth metadata org_id is a legacy fallback because complete_user_onboarding()
// writes only public.users while older accounts may carry org_id solely in
// auth metadata.
//
// This function is the client mirror of the server's my_org_id(), and the two must
// stay in lockstep. my_org_id() (migration 20260716174902) reads:
//
//   COALESCE(
//     (SELECT organization_id FROM users
//       WHERE id = auth.uid() AND role IS DISTINCT FROM 'internal_user'),
//     acting_org_id()
//   )
//
// so for an internal_user the database IGNORES users.organization_id entirely and
// uses the live acting session. We do the same here. Reading users.organization_id
// for an internal user would make the UI believe it is scoped to an org that RLS
// will not actually grant — every read would come back empty and every write would
// fail, with no error the user could interpret.
export async function resolveUserOrganization(session: Session): Promise<{
  organizationId: string | null
  role: string | null
  actingOrg: ActingOrgSession | null
}> {
  const { data } = await supabase
    .from("users")
    .select("organization_id, role")
    .eq("id", session.user.id)
    .maybeSingle()

  const role = data?.role ?? null

  if (role === "internal_user") {
    const actingOrg = await fetchActingOrgSession()
    return { organizationId: actingOrg?.organizationId ?? null, role, actingOrg }
  }

  return {
    organizationId:
      data?.organization_id || session.user.user_metadata?.org_id || null,
    role,
    actingOrg: null,
  }
}

export async function requireAuth(): Promise<AuthSession> {
  const now = Date.now()
  
  // Return cached session if valid and recent
  if (sessionCache && 
      !sessionCache.error && 
      sessionCache.auth && 
      (now - sessionCache.timestamp) < CACHE_DURATION_MS) {
    return sessionCache.auth
  }

  try {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error) {
      console.error('Auth error:', error)
      sessionCache = { auth: null, timestamp: now, error: true }
      throw redirect(buildSignInRedirect())
    }

    if (!session) {
      sessionCache = { auth: null, timestamp: now, error: true }
      throw redirect(buildSignInRedirect())
    }

    if (isPendingReportShareAccountSetup(session)) {
      sessionCache = { auth: null, timestamp: now, error: true }
      throw redirect("/invite/accept")
    }

    const { organizationId, actingOrg } = await resolveUserOrganization(session)

    const authSession: AuthSession = {
      session,
      userId: session.user.id,
      email: session.user.email || '',
      organizationId,
      hasOrganization: !!organizationId,
      actingOrg
    }

    // Cache the successful result
    sessionCache = { auth: authSession, timestamp: now, error: false }
    
    return authSession
  } catch (error) {
    // Clear cache on error and re-throw
    sessionCache = { auth: null, timestamp: now, error: true }
    throw error
  }
}

export async function requireOrganization(): Promise<AuthSession & { organizationId: string }> {
  const auth = await requireAuth()
  
  if (!auth.organizationId) {
    const { data: userData } = await supabase
      .from("users")
      .select("role")
      .eq("id", auth.userId)
      .maybeSingle()

    if (userData?.role === "report_viewer") {
      throw redirect("/reports")
    }

    // Internal staff have no org of their own by design; they pick one to act as.
    // Sending them to /create-organization would invite them to create a brand-new
    // organization, which is the last thing anyone wants here.
    if (userData?.role === "internal_user") {
      throw redirect("/select-organization")
    }

    throw redirect("/create-organization")
  }

  return {
    ...auth,
    organizationId: auth.organizationId
  }
}

export function invalidateSessionCache() {
  sessionCache = null
}

export class RouteError extends Error {
  constructor(
    message: string,
    public statusCode: number = 500,
    public details?: any
  ) {
    super(message)
    this.name = 'RouteError'
  }
}

export function throwRouteError(error: unknown): never {
  console.error('Route error:', error)
  
  if (error instanceof RouteError) {
    throw new Response(error.message, { 
      status: error.statusCode,
      statusText: error.message 
    })
  }
  
  if (error instanceof Error) {
    throw new Response(error.message, { 
      status: 500,
      statusText: 'Internal Server Error' 
    })
  }
  
  throw new Response('An unexpected error occurred', { 
    status: 500,
    statusText: 'Internal Server Error' 
  })
}
